import React from "react";
import { Card, CardContent, Typography, Stack, Box, Divider } from "@mui/material";

interface Totals {
  incomeActual: number;
  incomeBudgeted: number;
  expensesActual: number;
  expensesBudgeted: number;
}

export default function SummaryCards({ totals }: { totals: Totals }) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const expensesBudgeted = totals.expensesBudgeted;
  const expensesActual = totals.expensesActual;
  const netBudgeted = totals.incomeBudgeted - totals.expensesBudgeted;
  const netActual = totals.incomeActual - totals.expensesActual;

  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthProgress = daysInMonth ? now.getDate() / daysInMonth : 0;

  const budgetUsedRatio = expensesBudgeted > 0 ? expensesActual / expensesBudgeted : 0;
  const budgetUsedPercent = Math.round(budgetUsedRatio * 100);
  const monthProgressPercent = Math.round(monthProgress * 100);

  const onTrackSpending = budgetUsedRatio <= monthProgress;
  const savingsOnTrack = monthProgress === 0 ? true : netActual >= netBudgeted * monthProgress;
  const isOverBudget = netBudgeted < 0;

  return (
    <Card
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: 2,
        minHeight: 340,
        height: 340,
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="h3" fontWeight={700} color={isOverBudget ? "error.main" : "success.main"}>
            {formatCurrency(Math.abs(netBudgeted))}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {isOverBudget ? "Over budget" : "Left to budget"}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          justifyContent="center"
          alignItems={{ xs: "flex-start", sm: "stretch" }}
          sx={{ height: "100%" }}
        >
          <Stack
            spacing={0.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ flex: 1, justifyContent: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              Budgeted income
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatCurrency(totals.incomeBudgeted)}
            </Typography>
            <Typography variant="caption" color="success.main">
              This month: {formatCurrency(totals.incomeActual)}
            </Typography>
          </Stack>

          <Stack
            spacing={0.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ flex: 1, justifyContent: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              Budgeted expenses
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatCurrency(totals.expensesBudgeted)}
            </Typography>
            <Typography variant="caption" color="error.main">
              This month: {formatCurrency(totals.expensesActual)}
            </Typography>
          </Stack>

          <Stack
            spacing={0.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ flex: 1, justifyContent: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              Spending pace
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {`${isNaN(budgetUsedPercent) ? 0 : budgetUsedPercent}% spent • ${
                isNaN(monthProgressPercent) ? 0 : monthProgressPercent
              }% of month`}
            </Typography>
            <Typography variant="caption" color={onTrackSpending ? "success.main" : "error.main"}>
              {onTrackSpending ? "On track" : "Spending fast"}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
